/*
 * Listing scroll restoration.
 *
 * Opening a product from a collection and pressing back used to land the
 * shopper at the top of the grid instead of the row they had been looking at,
 * so they had to scroll all the way down again to carry on browsing.
 *
 * Browsers do restore scroll themselves, and measured on the dev preview
 * desktop Chrome gets it right on both paths - from the back/forward cache and
 * from a full reload. It is not something to rely on here though. It needs the
 * page to still be in the back/forward cache, and it needs the document to be
 * the same height on the way back as it was on the way out. Neither holds
 * across this store's traffic: iOS Safari and the in-app browsers a lot of it
 * arrives in evict pages early, and the review app injects a star rating into
 * every card after load, which changes the height of the grid underneath
 * whatever position the browser picked.
 *
 * So the position is recorded on the way out and re-applied on the way back,
 * anchored to the card the shopper actually opened rather than to a pixel
 * offset. The anchor is what survives those late height changes: the card is
 * found again in the DOM and put back at the same place on screen, so it does
 * not matter how much taller or shorter everything above it became in the
 * meantime.
 *
 * This runs alongside the browser's own restoration rather than replacing it.
 * history.scrollRestoration is deliberately left on 'auto': where the browser
 * gets it right this agrees and nothing moves, and where the browser gives up
 * this corrects it. Switching it to 'manual' would mean any failure in here
 * loses the position outright, which is worse than the bug being fixed.
 *
 * Pagination on this store is link based - "Show More" is a real link to
 * ?page=2 - so a listing URL always renders the same products. That is why
 * entries are keyed by the full path and query: page 2 restores page 2, and a
 * filtered listing restores its own position rather than the unfiltered one.
 */
(function () {
  'use strict';

  var KEY = 'civia:listing-scroll';
  var MAX_ENTRIES = 8;
  var MAX_AGE_MS = 30 * 60 * 1000;
  var SETTLE_MS = 2000; // keep correcting this long for late height changes
  var STABLE_MS = 400; // stop once the anchor has held still this long
  var RETRY_MS = 100;
  var TOLERANCE = 2; // px

  function listingUrl() {
    return window.location.pathname + window.location.search;
  }

  function read() {
    try {
      var raw = window.sessionStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      // Private mode, or storage is disabled. Restoration is a nicety; it must
      // never take the page down with it.
      return {};
    }
  }

  function write(map) {
    try {
      window.sessionStorage.setItem(KEY, JSON.stringify(map));
    } catch (error) {
      /* see read() */
    }
  }

  // A shopper can work through several listings in one visit, so keep the most
  // recent few rather than one, but do not let the key grow without limit.
  function prune(map) {
    var urls = Object.keys(map);
    if (urls.length <= MAX_ENTRIES) return map;
    urls.sort(function (a, b) {
      return (map[a].t || 0) - (map[b].t || 0);
    });
    for (var i = 0; i < urls.length - MAX_ENTRIES; i++) delete map[urls[i]];
    return map;
  }

  function handleOf(href) {
    var match = /\/products\/([^/?#]+)/.exec(href || '');
    if (!match) return null;
    // Handles are lowercase alphanumerics and hyphens. Anything else did not
    // come from a product link and must not reach querySelector.
    return /^[A-Za-z0-9_-]+$/.test(match[1]) ? match[1] : null;
  }

  function cardFor(handle) {
    if (!handle) return null;
    // The substring match narrows the field cheaply, but it is not conclusive:
    // one handle can be a prefix of another, and anchoring to the wrong card
    // would restore the wrong position. So confirm the path really ends at this
    // handle. Product URLs come in both forms, /products/x and
    // /collections/y/products/x, hence matching on the tail.
    var links = document.querySelectorAll('a[href*="/products/' + handle + '"]');
    for (var i = 0; i < links.length; i++) {
      var path = links[i].pathname || '';
      var at = path.lastIndexOf('/products/');
      if (at !== -1 && path.slice(at + 10).replace(/\/+$/, '') === handle) {
        return links[i].closest('.card-product, .product-item, .grid__item') || links[i];
      }
    }
    return null;
  }

  function scrollY() {
    return Math.round(window.scrollY || window.pageYOffset || 0);
  }

  function maxScroll() {
    return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  }

  function store(entry) {
    var map = read();
    map[listingUrl()] = entry;
    write(prune(map));
  }

  // Outbound: remember where the shopper was and which card they opened.
  // Listened for in the capture phase so a card handler that stops propagation
  // cannot cost the shopper their position. That means defaultPrevented cannot
  // be consulted here - nothing has had the chance to set it yet - so a click
  // that turns out not to navigate still records an entry. onPageHide below is
  // what reconciles that.
  function onClick(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (typeof event.button === 'number' && event.button !== 0) return;

    var target = event.target;
    if (!target || !target.closest) return;
    var link = target.closest('a[href*="/products/"]');
    if (!link) return;
    if (link.target && link.target !== '_self') return;

    var handle = handleOf(link.getAttribute('href'));
    if (!handle) return;

    var card = cardFor(handle) || link;
    store({
      y: scrollY(),
      handle: handle,
      offset: Math.round(card.getBoundingClientRect().top),
      product: link.pathname || '',
      t: Date.now()
    });
  }

  // Safety net, and the reconciliation point for onClick. Anything that leaves
  // the page without a product-link click we saw - a quick view that navigates,
  // a script-driven redirect, a tap on the logo - still gets its position
  // recorded here. And if the shopper kept scrolling after the click we did
  // see, that click's anchor no longer describes where they are, so it is
  // dropped in favour of the position they actually left from.
  function onPageHide() {
    var map = read();
    var url = listingUrl();
    var entry = map[url];
    var y = scrollY();

    if (entry && typeof entry.y === 'number' && Math.abs(entry.y - y) <= TOLERANCE) return;

    map[url] = { y: y, t: Date.now() };
    write(prune(map));
  }

  function cameBack() {
    try {
      var nav = window.performance.getEntriesByType('navigation')[0];
      if (nav && nav.type === 'back_forward') return true;
    } catch (error) {
      /* fall through to the legacy API below */
    }
    // Safari only grew the Navigation Timing Level 2 API recently.
    var legacy = window.performance && window.performance.navigation;
    return !!legacy && legacy.type === 2;
  }

  // Some in-app browsers report a back navigation as an ordinary one. If the
  // page we arrived from is the product this entry was created for, the shopper
  // came back from it whatever the navigation type says.
  function returnedFromProduct(entry) {
    if (!entry.product || !document.referrer) return false;
    try {
      var referrer = new URL(document.referrer);
      return referrer.origin === window.location.origin && referrer.pathname === entry.product;
    } catch (error) {
      return false;
    }
  }

  function restore(entry) {
    var stopped = false;
    var deadline = Date.now() + SETTLE_MS;
    var stableSince = 0;
    var events = ['wheel', 'touchstart', 'pointerdown', 'keydown'];

    function stop() {
      if (stopped) return;
      stopped = true;
      events.forEach(function (name) {
        window.removeEventListener(name, stop);
      });
    }

    // The shopper always outranks us. The moment they touch the page we are
    // done, so this can never fight them for the scroll position.
    events.forEach(function (name) {
      window.addEventListener(name, stop, { passive: true });
    });

    function targetY() {
      var card = cardFor(entry.handle);
      if (card && typeof entry.offset === 'number') {
        return Math.round(card.getBoundingClientRect().top + scrollY() - entry.offset);
      }
      return typeof entry.y === 'number' ? entry.y : null;
    }

    function apply() {
      if (stopped) return;

      var want = targetY();
      if (want === null) {
        stop();
        return;
      }
      want = Math.max(0, Math.min(want, maxScroll()));

      if (Math.abs(scrollY() - want) > TOLERANCE) {
        window.scrollTo(0, want);
        stableSince = 0;
      } else if (!stableSince) {
        stableSince = Date.now();
      }

      if (stableSince && Date.now() - stableSince >= STABLE_MS) {
        stop();
        return;
      }
      if (Date.now() >= deadline) {
        stop();
        return;
      }
      window.setTimeout(apply, RETRY_MS);
    }

    // Deliberately not requestAnimationFrame. A page can be restored while it
    // is not being painted - a background tab, a prerender, some in-app
    // browsers - and rAF does not run then, which would silently skip the
    // restore entirely on exactly the browsers this fix exists for. setTimeout
    // keeps running either way. There is no need to wait for the browser's own
    // attempt first: if it restores afterwards it lands on the same place, and
    // if it does not, the settle loop below still has the last word.
    apply();
  }

  function maybeRestore(force) {
    var map = read();
    var url = listingUrl();
    var entry = map[url];
    if (!entry) return;

    if (!entry.t || Date.now() - entry.t > MAX_AGE_MS) {
      delete map[url];
      write(map);
      return;
    }

    // A fresh visit to a listing must start at the top. Only a genuine return
    // to it restores.
    if (!force && !cameBack() && !returnedFromProduct(entry)) return;

    // Consumed either way, so the position is never re-applied to a later
    // fresh visit to the same URL.
    delete map[url];
    write(map);
    restore(entry);
  }

  document.addEventListener('click', onClick, true);
  window.addEventListener('pagehide', onPageHide);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      maybeRestore(false);
    });
  } else {
    maybeRestore(false);
  }

  // Restored from the back/forward cache: same document, so this is always a
  // return and the entry saved on the way out applies.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) maybeRestore(true);
  });
})();
